import { fetchContestProblem } from '@argoncs/common'
import { ContestProblemListSchema, ContestSchema, ContestProblemSchema, NewTeamSchema } from '@argoncs/types'
import { Type } from '@sinclair/typebox'
import { UnauthorizedError, badRequestSchema, conflictSchema, forbiddenSchema, notFoundSchema, unauthorizedSchema } from 'http-errors-enhanced'
import { verifyContestBegan, verifyContestNotBegan, verifyContestPublished, verifyContestRegistration } from '../auth/contest.auth.js'
import { verifyDomainScope } from '../auth/scope.auth.js'
import { userAuthHook } from '../hooks/authentication.hooks.js'
import { fetchContest, fetchContestProblemList, removeProblemFromContest, syncProblemToContest } from '../services/contest.services.js'
import { FastifyTypeBox } from '../types.js'
import { completeTeamInvitation, createTeam, createTeamInvitation } from '../services/team.services.js'
import { verifyTeamCaptain } from '../auth/team.auth.js'

async function contestProblemRoutes (problemRoutes: FastifyTypeBox): Promise<void> {
  problemRoutes.get(
    '/',
    {
      schema: {
        params: Type.Object({ contestId: Type.String() }),
        response: {
          200: ContestProblemListSchema,
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema
        }
      },
      onRequest: [userAuthHook, problemRoutes.auth([
        // @ts-expect-error: official documentation allows array in an array (and relation)
        [verifyContestRegistration, verifyContestBegan],
        verifyDomainScope(['contest.read'])]) as any]
    },
    async (request, reply) => {
      const { contestId } = request.params
      const problemList = await fetchContestProblemList(contestId)
      return await reply.status(200).send(problemList)
    }
  )

  problemRoutes.get(
    '/:problemId',
    {
      schema: {
        params: Type.Object({ contestId: Type.String(), problemId: Type.String() }),
        response: {
          200: ContestProblemSchema,
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema
        }
      },
      onRequest: [userAuthHook, problemRoutes.auth([
        // @ts-expect-error: official documentation allows array in an array (and relation)
        [verifyContestRegistration, verifyContestBegan],
        verifyDomainScope(['contest.read'])]) as any]
    },
    async (request, reply) => {
      const { contestId, problemId } = request.params
      const problem = await fetchContestProblem(problemId, contestId)
      return await reply.status(200).send(problem)
    }
  )

  problemRoutes.put(
    '/:problemId',
    {
      schema: {
        params: Type.Object({ contestId: Type.String(), problemId: Type.String() }),
        response: {
          200: Type.Object({ modified: Type.Boolean() }),
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema
        }
      },
      onRequest: [userAuthHook, problemRoutes.auth([verifyDomainScope(['contest.manage'])]) as any]
    },
    async (request, reply) => {
      const { contestId, problemId } = request.params
      const result = await syncProblemToContest(contestId, problemId)
      return await reply.status(200).send(result)
    }
  )

  problemRoutes.delete(
    '/:problemId',
    {
      schema: {
        params: Type.Object({ contestId: Type.String(), problemId: Type.String() }),
        response: {
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema
        }
      },
      onRequest: [userAuthHook, problemRoutes.auth([verifyDomainScope(['contest.manage'])]) as any]
    },
    async (request, reply) => {
      const { contestId, problemId } = request.params
      await removeProblemFromContest(contestId, problemId)
      return await reply.status(204).send()
    }
  )
}

async function contestTeamRoutes (teamRoutes: FastifyTypeBox): Promise<void> {
  teamRoutes.post(
    '/',
    {
      schema: {
        params: Type.Object({ contestId: Type.String() }),
        body: NewTeamSchema,
        response: {
          201: Type.Object({ teamId: Type.String() }),
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema,
          409: conflictSchema
        }
      },
      onRequest: [userAuthHook, teamRoutes.auth([verifyContestPublished, verifyContestNotBegan], { relation: 'and' }) as any]
    },
    async (request, reply) => {
      if (request.auth == null) {
        throw new UnauthorizedError('User not logged in')
      }

      const newTeam = request.body
      const { contestId } = request.params
      const result = await createTeam(newTeam, contestId, request.auth.id)
      return await reply.status(201).send(result)
    }
  )

  teamRoutes.post(
    '/:teamId/invitations',
    {
      schema: {
        params: Type.Object({ contestId: Type.String(), teamId: Type.String() }),
        body: Type.Object({ userId: Type.String() }),
        response: {
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema
        }
      },
      onRequest: [userAuthHook, teamRoutes.auth([verifyContestPublished, verifyContestNotBegan, verifyTeamCaptain], { relation: 'and' }) as any]
    },
    async (request, reply) => {
      const { userId } = request.body
      const { teamId, contestId } = request.params
      await createTeamInvitation(teamId, contestId, userId)

      return await reply.status(204).send()
    }
  )

  teamRoutes.post(
    '/:teamId/invitations/:invitationId',
    {
      schema: {
        params: Type.Object({ contestId: Type.String(), teamId: Type.String(), invitationId: Type.String() }),
        response: {
          400: badRequestSchema,
          401: unauthorizedSchema,
          403: forbiddenSchema,
          404: notFoundSchema
        }
      },
      onRequest: [userAuthHook, teamRoutes.auth([verifyContestPublished, verifyContestNotBegan], { relation: 'and' }) as any]
    },
    async (request, reply) => {
      if (request.auth == null) {
        throw new UnauthorizedError('User not logged in')
      }

      const { invitationId } = request.params
      await completeTeamInvitation(invitationId, request.auth.id)

      return await reply.status(204).send()
    }
  )
}

export async function contestRoutes (routes: FastifyTypeBox): Promise<void> {
  routes.get(
    '/:contestId',
    {
      schema: {
        params: Type.Object({ contestId: Type.String() }),
        response: {
          200: ContestSchema,
          400: badRequestSchema,
          404: notFoundSchema
        }
      },
      onRequest: [routes.auth([verifyDomainScope(['contest.read']), verifyContestPublished]) as any]
    },
    async (request, reply) => {
      const { contestId } = request.params
      const contest = fetchContest(contestId)
      return await reply.status(200).send(contest)
    })

  await routes.register(contestProblemRoutes, { prefix: '/:contestId/problems' })
  await routes.register(contestTeamRoutes, { prefix: '/:contestId/teams' })
}
